import { authRoutes } from '@/router/authRoutes'
import { publicRoutes } from '@/router/publicRotes'
import { testRoutes } from '@/router/testRoutes'
import { useAuthStore } from '@/stores/authStore'
import { useInviteStore } from '@/stores/inviteStore'
import { useLabStore } from '@/stores/labStore'
import { useModalStore } from '@/stores/modalStore'
import { useUserStore } from '@/stores/userStore'
import { getEnvTitlePrefix } from '@/globals/typescript/utils/envIndicator'
import {
  confirmLeaveUnsaved,
  consumeLeaveConfirmation,
  getBlockingClaims,
  markLeaveConfirmed,
} from '@/composables/useUnsavedChangesGuard'
import { createRouter, createWebHistory } from 'vue-router'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public
    ...publicRoutes,

    // Authenticated
    {
      path: '/app',
      name: 'App',
      component: () => import('@/views/AppLayoutView.vue'),
      meta: {
        requiresAuth: true,
      },
      redirect: { name: 'Labs' },
      children: [...testRoutes, ...authRoutes],
    },

    // Catch all
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      meta: {
        requiresAuth: false,
      },
      redirect: { name: 'Home' },
    },
  ],
})

// Auth guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const userStore = useUserStore()

  const waitAuth = setInterval(() => {
    if (!authStore.ready) {
      return
    }

    if (authStore.isLoggedIn && !userStore.currentUser) {
      return
    }

    if (
      authStore.isLoggedIn &&
      userStore.currentUser?.activeState !== 'active'
    ) {
      authStore.logout({ reason: 'deactivated' })
      clearInterval(waitAuth)
      return
    }

    clearInterval(waitAuth)

    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
    if (requiresAuth && !authStore.user) {
      next({ name: 'Home' })
    } else if (!requiresAuth && authStore.user) {
      next({ name: 'App' })
    } else {
      next()
    }
  }, 50)
})

// Permissions guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const userStore = useUserStore()

  if (!authStore.isLoggedIn) {
    return next()
  }

  if (!to.meta?.permission) {
    return next()
  }

  if (
    !userStore.currentUser ||
    !userStore.currentUser.canUser(to.meta?.permission)
  ) {
    return next({ name: 'App' })
  }

  return next()
})

// Unsaved changes guard
// Deliberately placed after the Auth and Permissions guards, so a navigation
// that would be rejected anyway never asks the user to throw work away first —
// and before the Title, Last seen and Data load guards, so a navigation the
// user cancels leaves no trace.
router.beforeEach(async (to, from, next) => {
  // A confirmed leave can be followed by a redirect from a guard below, which
  // starts a fresh navigation while the dirty surface is still mounted.
  // Without this, the user would be asked a second time for the same departure.
  if (consumeLeaveConfirmation()) {
    return next()
  }

  // Signed out already (a forced logout, e.g. a deactivated account): the
  // session is gone, so a cancellable "unsaved changes?" question would leave
  // a protected route on screen without one. Nothing left to protect.
  if (!useAuthStore().isLoggedIn) {
    return next()
  }

  if (!getBlockingClaims(to).length) {
    return next()
  }

  const shouldLeave = await confirmLeaveUnsaved()
  if (!shouldLeave) {
    return next(false)
  }

  markLeaveConfirmed()
  next()
})

// Clears a leave confirmation that was never consumed by a redirect, so it
// cannot leak into the user's next navigation.
router.afterEach((_to, _from, failure) => {
  // The user confirmed throwing the draft away and the navigation went through:
  // the modals holding it live in ModalHost, outside router-view, so they would
  // otherwise stay over the destination. afterEach also runs for an aborted or
  // superseded navigation (with the failure as its third argument) — the marker
  // is consumed either way, but only a navigation that succeeded clears the
  // modals, so a draft is never discarded by a departure that did not happen.
  if (consumeLeaveConfirmation() && !failure) {
    useModalStore().closeAll()
  }
})

// Title guard
router.beforeEach((to, from, next) => {
  const name = to.name
  const routeTitle = typeof name === 'string' ? name : String(name ?? '')
  document.title = `${getEnvTitlePrefix()}Lab Admin - ${routeTitle}`
  next()
})

// Last seen guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()

  if (authStore.isLoggedIn) {
    const userStore = useUserStore()
    userStore.updateLastSeen()
  }

  next()
})

// Data load guard
router.beforeEach(async (to, from, next) => {
  const checklist = []

  const authStore = useAuthStore()
  const userStore = useUserStore()
  const labAccountStore = useLabStore()
  const inviteStore = useInviteStore()

  // Add to checklist
  if (authStore.isLoggedIn) {
    // Lab accounts
    checklist.push('allLabAccountsLoaded')

    // Users
    if (userStore.currentUser?.canUser('users.view')) {
      checklist.push('allLabAdminUsersLoaded')
      checklist.push('allActiveInvitesLoaded')
    }
  }

  // Do the checklist
  const allPromises: Promise<unknown>[] = []
  if (checklist.includes('allLabAccountsLoaded')) {
    allPromises.push(labAccountStore.getAllLabAccounts())
  }
  if (checklist.includes('allLabAdminUsersLoaded')) {
    allPromises.push(userStore.getAllLabAdminUsers())
  }
  if (checklist.includes('allActiveInvitesLoaded')) {
    allPromises.push(inviteStore.getAllActiveInvites())
  }

  // Wait for checklist to complete
  await Promise.all(allPromises)

  next()
})

export default router

